// The right-click menu, rendered from the command catalogue.
//
// It knows no actions of its own. Give it a list of commands and a context; it prints what is enabled,
// dispatches what is clicked, and prints each shortcut beside its label so the menu teaches the
// keyboard rather than competing with it. That is how a menu stops drifting from the rest of the app:
// there is nothing in it to drift.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { keyHint, IS_MAC, type Command, type CmdCtx } from '../lib/commands.ts';

export interface MenuAt { x: number; y: number }

const GROUP_LABEL: Record<Command['group'], string> = {
  plan: 'Plannen', crew: 'Ploeg', edit: 'Bewerken', view: 'Weergave',
};

export function ContextMenu({ at, commands, ctx, onRun, onClose, extra }: {
  at: MenuAt;
  commands: Command[];
  ctx: CmdCtx;
  onRun: (c: Command) => void;
  onClose: () => void;
  /** Menu entries that are not catalogue commands — "toewijzen aan …", built from live crew. */
  extra?: { label: string; onPick: () => void; danger?: boolean; group: string }[];
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(at);

  // FLIP RATHER THAN OVERFLOW. Opened near the right or bottom edge the menu would render off-screen
  // and the planner would see half of it; measured after layout and pulled back inside the viewport.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      x: Math.min(at.x, window.innerWidth - r.width - 8),
      y: Math.min(at.y, window.innerHeight - r.height - 8),
    });
  }, [at.x, at.y]);

  useEffect(() => {
    const away = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    // `pointerdown`, not `click`: a click listener fires after the chip underneath has already
    // reacted, so the menu closed and the thing behind it also got pressed.
    window.addEventListener('pointerdown', away);
    window.addEventListener('keydown', key);
    return () => { window.removeEventListener('pointerdown', away); window.removeEventListener('keydown', key); };
  }, [onClose]);

  const groups = ['plan', 'crew', 'edit'] as const;
  const usable = commands.filter((c) => c.enabled(ctx));

  return (
    <div className="menu" role="menu" ref={ref} style={{ left: pos.x, top: pos.y }}>
      {groups.map((g) => {
        const inGroup = usable.filter((c) => c.group === g);
        const extraInGroup = (extra ?? []).filter((e) => e.group === g);
        if (inGroup.length === 0 && extraInGroup.length === 0) return null;
        return (
          <div key={g} className="menu-group">
            <div className="menu-title">{GROUP_LABEL[g]}</div>
            {inGroup.map((c) => (
              <button key={c.id} role="menuitem" className={`menu-item${c.danger ? ' danger' : ''}`}
                      onClick={() => { onRun(c); onClose(); }}>
                <span>{c.label(ctx)}</span>
                {c.keys && <kbd>{keyHint(c.keys, IS_MAC)}</kbd>}
              </button>
            ))}
            {extraInGroup.map((e) => (
              <button key={e.label} role="menuitem" className={`menu-item${e.danger ? ' danger' : ''}`}
                      onClick={() => { e.onPick(); onClose(); }}>
                <span>{e.label}</span>
              </button>
            ))}
          </div>
        );
      })}
      {usable.length === 0 && (extra ?? []).length === 0 && (
        <div className="menu-title" style={{ padding: '8px 12px' }}>Niets te doen met deze selectie.</div>
      )}
    </div>
  );
}
